import { Link, useNavigate } from "react-router-dom";
import { Star, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import CountdownPill from "@/components/CountdownTimer";
import VerifiedBadge from "@/components/VerifiedBadge";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const nav = useNavigate();

  const soldOut = product.sold_out || Number(product.stock ?? 1) <= 0;
  const CardWrap = soldOut ? "div" : Link;
  const discount = product.discount_percent || 0;
  const priceStr = `₹${product.price.toLocaleString("en-IN")}`;
  const origStr = product.original_price && product.original_price > product.price
    ? `₹${product.original_price.toLocaleString("en-IN")}` : null;

  const handleAdd = (e) => {
    e.preventDefault(); e.stopPropagation();
    addItem(product, 1);
    toast.success(`${product.name} added to cart`);
  };
  const handleBuy = (e) => {
    e.preventDefault(); e.stopPropagation();
    addItem(product, 1);
    nav("/checkout");
  };

  return (
    <CardWrap to={`/product/${product.id}`} data-testid={`product-card-${product.id}`} aria-disabled={soldOut || undefined}
      className={`product-card group block bg-white rounded-2xl border border-gray-200 overflow-hidden ${soldOut ? "cursor-not-allowed" : ""}`}>
      <div className="relative aspect-[4/5] overflow-hidden bg-gray-100">
        <img src={product.image_base64} alt={product.name} loading="lazy" decoding="async" className={`w-full h-full object-cover transition-transform duration-500 ${soldOut ? "opacity-60 grayscale" : "group-hover:scale-105"}`} />
        {soldOut && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span data-testid={`sold-out-badge-${product.id}`} className="px-4 py-1.5 rounded-full bg-gray-900/90 text-white text-xs font-bold uppercase tracking-[0.2em]">Sold Out</span>
          </div>
        )}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {product.is_boosted && (
            <div data-testid={`sponsored-badge-${product.id}`} className="px-2 py-1 rounded-full bg-[#F48E2E] text-white text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1">
              Sponsored
            </div>
          )}
          {discount > 0 && (
            <div data-testid="discount-badge" className="px-2 py-1 rounded-full bg-[#111827] text-white text-[10px] font-bold uppercase tracking-wider">-{discount}%</div>
          )}
          {product.cod_available === false && (
            <div data-testid="prepaid-badge" className="px-2 py-1 rounded-full bg-gray-800/85 text-white text-[10px] font-bold uppercase tracking-wider">Prepaid</div>
          )}
        </div>
        <div className="absolute top-3 right-3">
          <CountdownPill targetIso={product.expires_at} />
        </div>
      </div>
      <div className="p-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1">{product.category?.replace(/-/g, " ")}</div>
        <div className="font-medium text-[15px] text-gray-900 line-clamp-1">{product.name}</div>
        {product.vendor_business_name && (
          <div data-testid={`product-vendor-${product.id}`} className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-500">
            <span className="truncate">by {product.vendor_business_name}</span>
            {product.vendor_verified && <VerifiedBadge size="h-3.5 w-3.5" testid={`product-verified-${product.id}`} />}
          </div>
        )}
        <div className="flex items-center gap-1 mt-1">
          {[1,2,3,4,5].map(i => (
            <Star key={i} className={`h-3 w-3 ${i <= Math.round(product.rating || 4.5) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`} />
          ))}
          <span className="text-[11px] text-gray-500 ml-1">{product.rating?.toFixed(1) || "4.5"}</span>
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="font-display text-lg text-gray-900">{priceStr}</span>
          {origStr && <span className="text-sm text-gray-400 line-through">{origStr}</span>}
        </div>
        {soldOut ? (
          <div data-testid={`sold-out-label-${product.id}`} className="mt-3 h-9 rounded-full bg-gray-100 text-gray-500 text-xs font-semibold uppercase tracking-widest flex items-center justify-center">Sold Out</div>
        ) : (
        <div className="mt-3 flex gap-2">
          <Button onClick={handleAdd} data-testid={`add-to-cart-${product.id}`} variant="outline" size="sm" className="flex-1 rounded-full border-gray-300">
            <ShoppingCart className="h-3.5 w-3.5 mr-1" /> Add
          </Button>
          <Button onClick={handleBuy} data-testid={`buy-now-${product.id}`} size="sm" className="flex-1 rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white">Buy Now</Button>
        </div>
        )}
      </div>
    </CardWrap>
  );
}
